import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  isDevMode,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  BreakpointObserverService,
  CartDataService,
  CartService,
  ConfigurationService,
  HREF,
  ProductProcessingService,
  UserService,
} from '@core';

import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { HideHeaderWithHeightDirective } from 'libs/shared/src/lib/directives/hide-header-with-height.directive';
import { delay, filter, switchMap } from 'rxjs/operators';
import { DigitsGroupingPipe } from '../pipes/digits-grouping.pipe';
import { of } from 'rxjs';
import { ThemeService } from '../services/theme.service';

@Component({
  selector: 'lib-top-header',
  templateUrl: './top-header.component.html',
  styleUrl: './top-header.component.scss',
  imports: [
    CommonModule,
    MatToolbarModule,
    MatIconModule,
    DigitsGroupingPipe,
    RouterLink,
    HideHeaderWithHeightDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopHeaderComponent implements OnInit, AfterViewInit {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  viewMode = input<'default' | 'dock'>('default');
  clickLogo = output();
  headerHidded = output<boolean>();

  readonly themeService = inject(ThemeService);
  readonly isDevMode = isDevMode();
  router = inject(Router);
  private readonly cartDataService = inject(CartDataService);
  private readonly processingService = inject(ProductProcessingService);
  private readonly cartService = inject(CartService);
  public readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);
  readonly breakpointObserverService = inject(BreakpointObserverService);

  readonly href = HREF;
  readonly isTabletOrLower = this.breakpointObserverService.isTabletOrLower;
  readonly isDesktopOrBigger = this.breakpointObserverService.isDesktopOrBigger;
  readonly isMobile = this.breakpointObserverService.isMobile;

  // Balance is shown in Shopping Dollars only, no USD equivalent.
  ariaLabelBalanceStatus = computed(() => `Balance, ${this.userService.points()} ${this.rewardName()}`);
  isHeaderHidden = signal(false);

  readonly vm = computed(() => ({
    productCard: this.cartDataService.productDetailsCart(),
  }));
  private readonly cartProducts$ = toObservable(computed(() => this.vm().productCard?.products ?? []));
  filteredCartCount = signal<number>(0);

  logoLink = viewChild<ElementRef<HTMLElement>>('logoLink');

  ngOnInit(): void {
    this.fetchBucketProducts();
    this.bindRouteChangeToFocusOnLogoLink();
    this.setupCartCountFiltering();
  }

  ngAfterViewInit(): void {
    this.setFocusOnLogoLink();
  }

  // RCP-442: order history disabled
  // goToAccountOrders(): void {
  //   this.router.navigate([HREF.ORDERS]);
  // }

  goToAccount(): void {
    this.router.navigate([HREF.ACCOUNT]);
  }

  onToggleCart(): void {
    if (this.router.url?.includes(HREF.CHECKOUT)) {
      this.router.navigate([HREF.CART]);
    } else if (this.router.url?.includes(HREF.CART)) {
      return;
    } else {
      this.cartService.toggleCartDropdown();
    }
  }

  private fetchBucketProducts(): void {
    this.cartDataService.fetchProductCart().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  headerHidden(event: boolean | Event): void {
    this.isHeaderHidden.set(event as boolean);
    this.headerHidded.emit(event as boolean);
  }

  private setFocusOnLogoLink(): void {
    this.logoLink()?.nativeElement.focus();
  }

  private bindRouteChangeToFocusOnLogoLink(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        delay(200),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.setFocusOnLogoLink();
      });
  }
  private setupCartCountFiltering(): void {
    // Create a computed signal for products and convert to observable
    this.cartProducts$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((productList) => {
          if (productList.length > 0) {
            return this.processingService.filterOutAppleCareProductsBaseProduct(productList);
          } else {
            return of([]);
          }
        }),
      )
      .subscribe((filteredProducts) => {
        this.filteredCartCount.set(filteredProducts.length);
      });
  }
}
